import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Minus, Plus, Trash2, ShoppingBag, Tag, Home, Pencil, AlertTriangle, GripVertical } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useCart } from '../../context/CartContext';
import { Button } from '../ui/button';
import { formatPrice } from '../../lib/utils';
import { groupCartByRoom } from '../../lib/cartGroups';
import { quoteTracker } from '../../lib/consultSource';


function SortableCartItem({ item, getProductName, updateCartItem, removeFromCart, loading, setIsOpen, navigate }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1, zIndex: isDragging ? 10 : 'auto' };
  return (
    <div ref={setNodeRef} style={style} className="flex gap-3 p-4 bg-neutral-50 group" data-testid={`cart-item-${item.id}`}>
      <div {...attributes} {...listeners} className="flex items-center cursor-grab text-neutral-200 hover:text-neutral-400 transition-colors touch-none" title="Drag to reorder">
        <GripVertical className="w-4 h-4" />
      </div>
      <div className="w-14 h-14 bg-neutral-200 flex-shrink-0 flex items-center justify-center text-[10px] text-neutral-500 rounded">
        {item.config.width}"x{item.config.height}"
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-neutral-900 truncate text-sm" data-testid="cart-item-title">{item.windowLabel || getProductName(item.config.product_type)}</p>
        <p className="text-xs text-neutral-500">{getProductName(item.config.product_type)}</p>
        <p className="text-xs text-neutral-400 font-mono">{item.config.width}" W x {item.config.height}" H</p>
        <div className="flex items-center justify-between mt-1.5">
          <div className="flex items-center gap-1.5">
            <button onClick={() => updateCartItem(item.id, Math.max(1, item.quantity - 1))} disabled={loading || item.quantity <= 1} className="p-0.5 text-neutral-400 hover:text-neutral-900 disabled:opacity-50"><Minus className="w-3.5 h-3.5" /></button>
            <span className="font-mono text-sm w-5 text-center">{item.quantity}</span>
            <button onClick={() => updateCartItem(item.id, item.quantity + 1)} disabled={loading} className="p-0.5 text-neutral-400 hover:text-neutral-900 disabled:opacity-50"><Plus className="w-3.5 h-3.5" /></button>
          </div>
          <span className="font-mono font-medium text-sm">{formatPrice(item.config.total_price)}</span>
        </div>
      </div>
      <div className="flex flex-col items-center self-start">
        <button onClick={() => { setIsOpen(false); navigate(`/products/${item.config.product_type || 'roller-shades'}?editCartItem=${item.id}`); }} disabled={loading} className="p-1.5 text-neutral-400 hover:text-neutral-900 transition-colors" title="Edit" data-testid={`cart-item-edit-${item.id}`}><Pencil className="w-3.5 h-3.5" /></button>
        <button onClick={() => removeFromCart(item.id)} disabled={loading} className="p-1.5 text-neutral-400 hover:text-red-600 transition-colors" data-testid={`cart-item-remove-${item.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}

// Compact "Your rooms" overview: room · shade count · subtotal, overall total below (uses existing room groups)
const RoomSummaryCard = ({ groups, total }) => (
  <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50/50 p-4" data-testid="cart-room-summary">
    <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-800 mb-2">Your rooms</p>
    <div className="divide-y divide-amber-100">
      {groups.map((g) => (
        <div key={g.room} className="flex items-center justify-between gap-3 py-1.5 text-sm" data-testid={`cart-room-summary-${g.room.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
          <span className="flex items-center gap-2 min-w-0">
            <Home className="w-3.5 h-3.5 text-amber-600 shrink-0" />
            <span className="truncate text-neutral-900">{g.room}</span>
            <span className="text-xs text-neutral-500 shrink-0">{g.quantity} {g.quantity === 1 ? 'shade' : 'shades'}</span>
          </span>
          <span className="font-mono text-neutral-800 shrink-0">{formatPrice(g.subtotal)}</span>
        </div>
      ))}
    </div>
    <div className="mt-2 pt-2 border-t border-amber-200 flex items-center justify-between text-sm">
      <span className="font-medium text-neutral-900">Total</span>
      <span className="font-mono font-semibold text-neutral-900" data-testid="cart-room-summary-total">{formatPrice(total)}</span>
    </div>
  </div>
);

export const CartPanel = () => {
  const { cart, isOpen, setIsOpen, updateCartItem, removeFromCart, clearCart, loading, bulkDiscounts, appliedDiscount, refreshCart } = useCart();
  const navigate = useNavigate();
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const allItemIds = (cart.items || []).map(i => i.id);

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = allItemIds.indexOf(active.id);
    const newIdx = allItemIds.indexOf(over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const newOrder = arrayMove(allItemIds, oldIdx, newIdx);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const sessionId = localStorage.getItem('cart_session_id') || '';
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token') || '';
      await fetch(`${API}/api/cart/reorder?session_id=${sessionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ item_ids: newOrder })
      });
      // Re-fetch cart
      refreshCart?.();
    } catch { /* ignore */ }
  }, [allItemIds, refreshCart]);

  const getProductName = (type) => {
    const names = {
      'roller-shades': 'Roller Shade',
      'motorized-drapes': 'Motorized Drapes',
      'roman-shades': 'Roman Shade',
    };
    return names[type] || 'Custom Product';
  };

  const handleCheckout = () => {
    setIsOpen(false);
    quoteTracker.mark('Cart: Request Quote');
    navigate('/quote');
  };

  const handleClearCart = async () => {
    try {
      setClearing(true);
      await clearCart();
      // Clear session shades from all product types
      ['roller-shades', 'motorized-drapes', 'roman-shades'].forEach(t => sessionStorage.removeItem(`apollo_session_shades_${t}`));
      setShowClearConfirm(false);
    } catch {
      // ignore
    } finally {
      setClearing(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
            data-testid="cart-backdrop"
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col"
            data-testid="cart-panel"
          >
            <div className="flex items-center justify-between p-6 border-b border-neutral-100">
              <div className="flex items-center gap-3">
                <ShoppingBag className="w-5 h-5 text-neutral-600" strokeWidth={1.5} />
                <h2 className="font-serif text-xl">Your Quote</h2>
                <span className="text-sm text-neutral-500">({cart.items?.length || 0} items)</span>
              </div>
              <div className="flex items-center gap-1">
                {cart.items?.length > 0 && (
                  <button
                    onClick={() => setShowClearConfirm(true)}
                    className="p-2 text-neutral-300 hover:text-red-500 transition-colors"
                    title="Clear entire cart"
                    data-testid="clear-cart-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <button onClick={() => setIsOpen(false)} className="p-2 text-neutral-400 hover:text-neutral-900 transition-colors" data-testid="close-cart">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Clear Cart Confirmation */}
            <AnimatePresence>
              {showClearConfirm && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden border-b border-red-100"
                >
                  <div className="p-4 bg-red-50 flex items-center gap-3" data-testid="clear-cart-confirm">
                    <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-red-800">Clear all {cart.items?.length} item{cart.items?.length !== 1 ? 's' : ''}?</p>
                      <p className="text-xs text-red-600 mt-0.5">This cannot be undone.</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => setShowClearConfirm(false)} className="h-7 text-xs" data-testid="clear-cart-cancel">
                        Cancel
                      </Button>
                      <Button size="sm" onClick={handleClearCart} disabled={clearing} className="h-7 text-xs bg-red-600 hover:bg-red-700 text-white" data-testid="clear-cart-confirm-btn">
                        {clearing ? 'Clearing...' : 'Clear Cart'}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex-1 overflow-y-auto p-6">
              {cart.items?.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center" data-testid="empty-cart">
                  <ShoppingBag className="w-12 h-12 text-neutral-300 mb-4" strokeWidth={1} />
                  <p className="text-neutral-500 mb-4">Your quote is empty</p>
                  <Button onClick={() => { setIsOpen(false); navigate('/products'); }} className="btn-primary" data-testid="start-designing-btn">
                    Browse Products
                  </Button>
                </div>
              ) : (
                <>
                <RoomSummaryCard groups={groupCartByRoom(cart.items)} total={cart.total} />
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={allItemIds} strategy={verticalListSortingStrategy}>
                <div className="space-y-6">
                  {groupCartByRoom(cart.items).map((group) => (
                  <div key={group.room} className="space-y-2" data-testid={`cart-room-group-${group.room.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                    <div className="flex items-center justify-between gap-3 px-1">
                      <p className="flex items-center gap-2 font-medium text-neutral-900 text-sm truncate">
                        <Home className="w-4 h-4 text-amber-600 shrink-0" />
                        <span className="truncate" data-testid="cart-room-name">{group.room}</span>
                        <span className="text-xs text-neutral-400 font-normal shrink-0">{group.quantity} {group.quantity === 1 ? 'shade' : 'shades'}</span>
                      </p>
                      <span className="font-mono text-sm text-neutral-700 shrink-0" data-testid="cart-room-subtotal">{formatPrice(group.subtotal)}</span>
                    </div>
                  {group.items.map((item) => (
                    <SortableCartItem key={item.id} item={item} getProductName={getProductName} updateCartItem={updateCartItem} removeFromCart={removeFromCart} loading={loading} setIsOpen={setIsOpen} navigate={navigate} />
                  ))}
                  </div>
                  ))}
                </div>
                  </SortableContext>
                </DndContext>
                </>
              )}
            </div>

            {cart.items?.length > 0 && (
              <div className="border-t border-neutral-100 p-6 space-y-4">
                {/* Bulk Discount Banner */}
                {bulkDiscounts.enabled && !appliedDiscount && bulkDiscounts.tiers?.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                    <p className="font-medium text-amber-800 flex items-center gap-1">
                      <Tag className="w-4 h-4" />
                      Bulk Discounts Available!
                    </p>
                    <p className="text-amber-700 text-xs mt-1">
                      Add {bulkDiscounts.tiers[0]?.min_items - (cart.items?.reduce((sum, item) => sum + (item.quantity || 1), 0) || 0)} more item(s) to save {bulkDiscounts.tiers[0]?.discount_percent}%
                    </p>
                  </div>
                )}
                
                {/* Applied Discount */}
                {appliedDiscount && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="font-medium text-green-800 flex items-center gap-1 text-sm">
                      <Tag className="w-4 h-4" />
                      Bulk Discount Applied! ({appliedDiscount.tier.discount_percent}% off)
                    </p>
                    <p className="text-green-700 text-xs mt-1">
                      You're saving {formatPrice(appliedDiscount.discountAmount)} on {appliedDiscount.totalItems} items
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-neutral-600">Subtotal</span>
                  <span className={`font-mono text-lg ${appliedDiscount ? 'line-through text-neutral-400' : 'font-medium'}`}>
                    {formatPrice(cart.total)}
                  </span>
                </div>
                
                {appliedDiscount && (
                  <div className="flex items-center justify-between">
                    <span className="text-green-600 font-medium">Discounted Total</span>
                    <span className="font-mono text-xl font-bold text-green-600">
                      {formatPrice(appliedDiscount.discountedTotal)}
                    </span>
                  </div>
                )}
                
                <p className="text-xs text-neutral-400">Tax calculated at checkout</p>
                <Button onClick={() => { setIsOpen(false); navigate('/checkout'); }} className="w-full btn-primary" disabled={loading} data-testid="checkout-btn">
                  Proceed to Checkout
                </Button>
                <Button onClick={handleCheckout} variant="outline" className="w-full" disabled={loading} data-testid="quote-btn">
                  Request Quote Only
                </Button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
